import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, TextField, Button, InputAdornment, CircularProgress,
} from '@mui/material';
import { useToast } from './ui';

// Führt Schritt für Schritt durch die Zutaten eines Gerichts — jede Zutat
// wird einzeln mit ihrer Menge bestätigt und sofort als eigener
// food_consumed-Eintrag gespeichert ("erst Reis, dann Sauce").
export default function DishLoggingDialog({ open, onClose, dish, mealtime, date, onLogged }) {
  const [index,    setIndex]    = useState(0);
  const [grams,    setGrams]    = useState('100');
  const [saving,   setSaving]   = useState(false);
  const { showToast } = useToast();

  // Nur beim Öffnen bzw. bei einem *anderen* Gericht zurücksetzen. Vorher hing
  // der Effekt an der Objekt-Identität von `dish` — jedes Re-Render des Eltern-
  // Screens (z. B. durch die Kalorien-Aktualisierung nach dem Erfassen einer
  // Zutat) sprang damit zurück auf die erste Zutat.
  const dishKey = dish ? (dish.id ?? dish.name) : null;
  useEffect(() => {
    if (open) { setIndex(0); setGrams('100'); }
  }, [open, dishKey]);

  if (!dish) return null;

  const components = dish.components || [];
  const total = components.length;
  const safeIndex = Math.min(index, Math.max(0, total - 1));
  const component = components[safeIndex];
  const gramsNum = Number(grams);
  const isValid = Number.isFinite(gramsNum) && gramsNum > 0;
  const kcal = component && isValid ? Math.round((Number(component.kcal_per_100g) / 100) * gramsNum) : 0;

  const scaled = (per100) => (component && isValid ? Math.round((Number(per100 || 0) / 100) * gramsNum) : 0);

  const advance = () => {
    if (safeIndex + 1 < total) {
      setIndex(safeIndex + 1);
      setGrams('100');
    } else {
      onClose();
    }
  };

  const handleAdd = async () => {
    const token = localStorage.getItem('token');
    if (!token || !component || !isValid) return;

    setSaving(true);
    try {
      const res = await fetch('/api/users/add-food-consumed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: `${dish.name} – ${component.name}`,
          quantity: gramsNum,
          unit: 'g',
          mealtime,
          kcal,
          date,
          protein_g: scaled(component.protein_g),
          fat_g: scaled(component.fat_g),
          carbs_g: scaled(component.carbs_g),
        }),
      });
      if (res.ok) {
        showToast(`„${component.name}" hinzugefügt (${kcal} kcal)`, { severity: 'success' });
        onLogged?.(kcal);
        advance();
      } else {
        showToast('Fehler beim Erfassen der Zutat.', { severity: 'error' });
      }
    } catch {
      showToast('Verbindungsfehler.', { severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {dish.name}
        <Typography variant="body2" color="text.secondary">
          Zutat {safeIndex + 1} von {total}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <Typography variant="h6">{component?.name}</Typography>
        <TextField
          fullWidth
          autoFocus
          label="Menge"
          value={grams}
          onChange={(e) => setGrams(e.target.value.replace(/[^\d.,]/g, ''))}
          onFocus={(e) => e.target.select()}
          slotProps={{
            htmlInput: { inputMode: 'decimal' },
            input: {
              endAdornment: <InputAdornment position="end">g</InputAdornment>,
            },
          }}
        />
        <Typography variant="body2" color="text.secondary">
          ≈ {kcal} kcal
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={{ flex: 1 }} disabled={saving}>
          Abbrechen
        </Button>
        <Button onClick={handleAdd} variant="contained" disabled={!isValid || saving} sx={{ flex: 1 }}>
          {saving
            ? <CircularProgress size={20} color="inherit" />
            : (safeIndex + 1 < total ? 'Hinzufügen & weiter' : 'Hinzufügen & fertig')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
